import * as store from "../state/store.js";
import type { DxfUnit, SidebarPosition, Theme } from "../state/types.js";
import { codeFromUnit } from "./units.js";

// Persist a small bag of user-visible editor preferences to localStorage so
// the next visit rehydrates the same theme / grid / sidebar layout / units
// the user last chose. Anything model-related (entities, layers, history) is
// deliberately NOT stored — a fresh visit starts on an empty canvas.

const STORAGE_KEY = "dxf-editor-prefs";
const VERSION = 1;

interface StoredPrefs {
  version: number;
  theme?: Theme;
  sidebarPosition?: SidebarPosition;
  sidebarVisible?: boolean;
  showGrid?: boolean;
  showGridRuler?: boolean;
  dxfUnit?: DxfUnit;
  viewerUnit?: DxfUnit;
  gridUnit?: DxfUnit;
  gridSpacing?: number;
}

const VALID_UNIT: Record<string, DxfUnit> = {
  mm: "mm",
  cm: "cm",
  m: "m",
  in: "in",
  ft: "ft",
};

function readStored(): StoredPrefs | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredPrefs;
    if (!parsed || typeof parsed !== "object") return null;
    if (parsed.version !== VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

// Called once on host connect. Applies whatever was in localStorage on top of
// the store's defaults, then returns so subsequent writes can be captured.
export function loadPreferences(): void {
  const p = readStored();
  if (!p) return;

  store.batch(() => {
    if (p.theme === "dark" || p.theme === "light") store.write.theme(p.theme);
    if (p.sidebarPosition === "left" || p.sidebarPosition === "right") {
      store.write.sidebarPosition(p.sidebarPosition);
    }
    // sidebarVisible is not restored here — the load/close ops own that
    // signal (auto-open on load, auto-hide on close). Restoring it would
    // fight that contract on first paint.
    if (typeof p.showGrid === "boolean") store.write.showGrid(p.showGrid);
    if (typeof p.showGridRuler === "boolean") store.write.showGridRuler(p.showGridRuler);
    if (p.dxfUnit && VALID_UNIT[p.dxfUnit]) {
      store.write.dxfUnit(p.dxfUnit);
      store.write.dxfUnitCode(codeFromUnit(p.dxfUnit));
    }
    if (p.viewerUnit && VALID_UNIT[p.viewerUnit]) store.write.viewerUnit(p.viewerUnit);
    if (p.gridUnit && VALID_UNIT[p.gridUnit]) store.write.gridUnit(p.gridUnit);
    if (typeof p.gridSpacing === "number" && p.gridSpacing > 0) {
      store.write.gridSpacing(p.gridSpacing);
    }
  });
}

// Called by dxf-editor.ts inside an effect() so any change to the tracked
// signals writes out a fresh snapshot. Serialisation is cheap; localStorage
// writes happen at effect frequency, which coalesces microtask-batched writes.
export function savePreferences(): void {
  const snap: StoredPrefs = {
    version: VERSION,
    theme: store.theme(),
    sidebarPosition: store.sidebarPosition(),
    showGrid: store.showGrid(),
    showGridRuler: store.showGridRuler(),
    dxfUnit: store.dxfUnit(),
    viewerUnit: store.viewerUnit(),
    gridUnit: store.gridUnit(),
    gridSpacing: store.gridSpacing(),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(snap));
  } catch {
    // localStorage can throw in private-mode Safari or when quota is full.
    // Preferences are a UX nice-to-have, not something to surface as an error.
  }
}
